import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Hammer, Factory, Home as HomeIcon } from "lucide-react";

import { SERVICES, STATS } from "@/lib/site";
import StatsCounter from "@/components/StatsCounter";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";

const HERO_IMG =
  "/images/mn73hhiy.png";
const ABOUT_IMG =
  "/images/9ae3kktf.png";

const ICONS = {
  "industrial-resinous": Factory,
  "residential-flooring": HomeIcon,
  "food-grade-urethane": ShieldCheck,
  "exterior-stucco": Hammer,
};

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title={null}
        description="Industrial resinous, residential epoxy chip-flake, food-grade urethane and exterior stucco contractor serving the Greater Toronto Area."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden border-b border-zinc-800">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40"
          aria-hidden
        />
        <div className="absolute inset-0 grain-overlay" aria-hidden />

        <div className="container-max relative z-10 py-32">
          <div className="max-w-4xl">
            <div className="eyebrow" data-testid="hero-eyebrow">
              Industrial Surface Specialists · Greater Toronto Area
            </div>
            <h1
              data-testid="hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-8xl xl:text-9xl uppercase tracking-wider text-zinc-50 mt-6 leading-[0.9]"
            >
              Built to <span className="text-[#FF5A00]">withstand</span>.
              <br />
              Engineered to <span className="text-[#FF5A00]">last</span>.
            </h1>
            <p className="text-zinc-300 text-lg lg:text-xl mt-8 max-w-2xl leading-relaxed">
              Vision Era Construction installs heavy-duty resinous flooring,
              epoxy systems, food-grade cementitious urethane and exterior
              stucco for the operations that can&apos;t afford downtime.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="btn-primary"
              >
                Request a Quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="btn-secondary"
              >
                Our Capabilities
              </Link>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex absolute bottom-10 right-10 z-10 items-center gap-3 text-zinc-500 font-heading text-[11px] tracking-[0.3em] uppercase">
          <span className="w-12 h-px bg-zinc-700" />
          Scroll
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#FF5A00] py-5 overflow-hidden border-b border-zinc-800">
        <div className="flex whitespace-nowrap animate-marquee">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6 shrink-0">
              {[
                "Exterior Stucco",
                "Epoxy Flooring",
                "Industrial Resinous",
                "Food-Grade Urethane",
                "Polished Concrete",
                "USDA Compliant",
                "OSHA Certified",
              ].map((t) => (
                <span
                  key={t + i}
                  className="font-display text-2xl tracking-wider uppercase text-zinc-950"
                >
                  ◆ {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="section-pad" data-testid="services-overview">
        <div className="container-max">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="eyebrow">What We Install</div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-wider text-zinc-50 mt-4">
                Four pillars.
                <br />
                Zero compromise.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="text-zinc-300 hover:text-[#FF5A00] font-heading tracking-[0.2em] uppercase text-sm inline-flex items-center gap-2"
            >
              All Services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {SERVICES.map((s, idx) => {
              const Icon = ICONS[s.slug] || HomeIcon;
              const span =
                idx === 0
                  ? "md:col-span-7"
                  : idx === 1
                    ? "md:col-span-5"
                    : idx === 2
                      ? "md:col-span-5"
                      : "md:col-span-7";
              return (
                <Link
                  to="/services"
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className={`group relative overflow-hidden border border-zinc-800 hover:border-[#FF5A00] transition-colors min-h-[340px] flex ${span}`}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-110 transition-transform duration-700"
                    style={{ backgroundImage: `url(${s.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/10" />
                  <div className="relative p-8 lg:p-10 flex flex-col justify-end w-full">
                    <Icon className="w-8 h-8 text-[#FF5A00] mb-4" />
                    <h3 className="font-display text-3xl lg:text-4xl tracking-wider uppercase text-zinc-50">
                      {s.title}
                    </h3>
                    <p className="text-zinc-400 mt-3 max-w-lg leading-relaxed">
                      {s.blurb}
                    </p>
                    <span className="mt-5 font-heading tracking-[0.2em] uppercase text-xs text-[#FF5A00] inline-flex items-center gap-2">
                      Explore <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <StatsCounter stats={STATS} />

      {/* ABOUT TEASER */}
      <section className="section-pad" data-testid="about-teaser">
        <div className="container-max grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div
              className="aspect-[4/5] bg-cover bg-center"
              style={{ backgroundImage: `url(${ABOUT_IMG})` }}
            />
            <div className="absolute -bottom-6 -right-6 bg-[#FF5A00] text-zinc-950 p-6 max-w-[240px] hidden md:block">
              <div className="font-display text-3xl tracking-wider leading-tight">
                Serving the GTA
              </div>
              <div className="font-heading text-xs tracking-[0.25em] uppercase mt-1">
                Toronto · Peel · York · Durham · Halton
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="eyebrow">Who We Are</div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 leading-[0.95]">
              A crew of installers,
              <br />
              not a sales office.
            </h2>
            <p className="text-zinc-400 mt-6 leading-relaxed text-lg">
              Vision Era was founded by trade veterans who got tired of watching
              cheap coatings fail after one wash-down. We&apos;re a full-service
              industrial flooring &amp; envelope contractor working across the
              Greater Toronto Area.
            </p>
            <ul className="mt-8 space-y-3 text-zinc-300">
              {[
                "Manufacturer-certified applicators on every crew",
                "In-house surface prep — shot blasting, grinding, scarifying",
                "Detailed shop drawings & substrate testing before mobilization",
                "Weekend / shutdown installs to protect your production calendar",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="text-[#FF5A00] font-display text-xl leading-none">
                    →
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <Link to="/about" data-testid="about-cta" className="btn-secondary mt-10">
              About Vision Era <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS removed per request */}

      {/* QUOTE FORM CTA */}
      <section className="section-pad" data-testid="home-quote">
        <div className="container-max grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow">Project Intake</div>
            <h2 className="font-display text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 leading-[0.95]">
              Get a real quote
              <br />
              from a real estimator.
            </h2>
            <p className="text-zinc-400 mt-6 leading-relaxed text-lg">
              Send a few details about your facility. Our team will reply within
              one business day with a scope, line-item pricing range, and
              recommended system specs.
            </p>
            <ul className="mt-8 space-y-3 text-zinc-300">
              {[
                "No-obligation site walk for projects over 10,000 sq ft",
                "Coordinated phasing & containment plans",
                "Manufacturer system warranties up to 10 years",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <span className="text-[#FF5A00] font-display text-xl leading-none">→</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
}
